import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from './Toast';
import PaymentMethods, { PaymentMethod } from './PaymentMethods';
import MobilePayment from './MobilePayment';
import './TicketPurchase.css';
import '../styles/payment-progress.css';
import { apiService, Event as ApiEvent, TicketCategory, TicketPurchaseRequest, MultiTicketPurchaseRequest, IndividualTicket, CheckoutSessionDto } from '../services/api';

// Local augmentation: some events include a top-level checkoutUrl property used by older events
type Event = ApiEvent & { checkoutUrl?: string };

interface TicketPurchaseProps {
  eventId: string;
}

const TicketPurchase: React.FC<TicketPurchaseProps> = ({ eventId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toasts, removeToast, showError, showSuccess } = useToast();
  const [event, setEvent] = useState<Event | null>(null);
  const [selectedTickets, setSelectedTickets] = useState<Record<string, number>>({});
  
  // Estado do fluxo de pagamento
  const [purchaseFlow, setPurchaseFlow] = useState<'form' | 'payment-methods' | 'mobile-payment' | 'processing'>('form');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  
  // Novo estado para armazenar informações por ticket
  const [ticketUserInfo, setTicketUserInfo] = useState<Record<string, {
    name: string;
    email: string;
    phone: string;
    dateOfBirth: string;
    guardianName: string;
    guardianEmail: string;
    guardianPhone: string;
  }>>({});
  
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [checkoutSession, setCheckoutSession] = useState<CheckoutSessionDto | null>(null);

  // Enhanced payment progress state
  const [paymentProgress, setPaymentProgress] = useState({
    step: '',
    progress: 0,
    isActive: false
  });

  // Refs para scroll automático
  const formRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const purchaseSummaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadEvent = async () => {
      try {
        setLoading(true);
        const eventData = await apiService.getEvent(eventId);
        setEvent(eventData);
        
        // Check if any ticket category requires age verification
        const requiresVerification = eventData.ticketCategories?.some(
          category => category.ageRestrictions?.ageVerificationRequired
        );
        setShowAgeVerification(requiresVerification || false);
      } catch (err: any) {
        showError('Failed to load event details');
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [eventId, showError]);

  // Update progress bar width when progress changes
  useEffect(() => {
    const progressBar = document.querySelector('.payment-progress-fill');
    if (progressBar && paymentProgress.isActive) {
      (progressBar as HTMLElement).style.setProperty('--progress-width', `${paymentProgress.progress}%`);
    }
  }, [paymentProgress.progress, paymentProgress.isActive]);

  const calculateAge = (dateOfBirth: string): number => {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleTicketQuantityChange = (categoryId: string, quantity: number) => {
    const category = event?.ticketCategories?.find(c => c.id === categoryId);
    if (!category) return;

    // Validate quantity limits
    if (quantity < 0) quantity = 0;
    if (category.maxQuantity && quantity > category.maxQuantity) {
      quantity = category.maxQuantity;
    }

    // Check availability
    const available = (category.maxAttendees || Infinity) - (category.currentSold || 0);
    if (quantity > available) {
      quantity = available;
    }

    setSelectedTickets(prev => ({
      ...prev,
      [categoryId]: quantity
    }));

    // Initialize or clean up ticket user info based on quantity
    setTicketUserInfo(prev => {
      const newInfo = { ...prev };
      
      if (quantity > 0) {
        // Initialize info for each ticket in this category
        for (let i = 0; i < quantity; i++) {
          const ticketKey = `${categoryId}_${i}`;
          if (!newInfo[ticketKey]) {
            newInfo[ticketKey] = {
              name: '',
              email: '',
              phone: '',
              dateOfBirth: '',
              guardianName: '',
              guardianEmail: '',
              guardianPhone: ''
            };
          }
        }
        
        // Remove excess ticket forms if quantity decreased
        Object.keys(newInfo).forEach(key => {
          if (key.startsWith(`${categoryId}_`)) {
            const ticketIndex = parseInt(key.split('_')[1]);
            if (ticketIndex >= quantity) {
              delete newInfo[key];
            }
          }
        });
        
        // Scroll to the first form of this category after a short delay
        setTimeout(() => {
          const firstTicketKey = `${categoryId}_0`;
          const formElement = formRefs.current[firstTicketKey];
          if (formElement) {
            formElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest' 
            });
          }
        }, 100);
      } else {
        // Remove all info for tickets that are no longer selected
        Object.keys(newInfo).forEach(key => {
          if (key.startsWith(`${categoryId}_`)) {
            delete newInfo[key];
          }
        });
      }
      return newInfo;
    });
  };

  const updateTicketUserInfo = (ticketKey: string, field: string, value: string) => {
    setTicketUserInfo(prev => ({
      ...prev,
      [ticketKey]: {
        ...prev[ticketKey],
        [field]: value
      }
    }));
  };

  const getTotalCost = (): number => {
    if (!event?.ticketCategories) return 0;

    return Object.entries(selectedTickets).reduce((total, [categoryId, quantity]) => {
      const category = event.ticketCategories?.find(c => c.id === categoryId);
      if (category && !category.isFree) {
        return total + (category.price * quantity);
      }
      return total;
    }, 0);
  };

  const getTotalTickets = (): number => {
    return Object.values(selectedTickets).reduce((total, quantity) => total + quantity, 0);
  };

  const handlePurchase = async () => {
    if (!event) return;

    // Store current event ID for potential retry
    sessionStorage.setItem('currentEventId', event.id);

    // Validate ticket selection
    const totalTickets = getTotalTickets();
    if (totalTickets === 0) {
      showError(t('purchase.errorNoTickets'));
      // Scroll to purchase summary to show error
      setTimeout(() => {
        purchaseSummaryRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
      return;
    }

    // Validate user info for each selected ticket
    const selectedTicketKeys = Object.keys(ticketUserInfo);
    if (selectedTicketKeys.length === 0) {
      showError(t('purchase.fillAllTicketForms'));
      // Scroll to purchase summary to show error
      setTimeout(() => {
        purchaseSummaryRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
      return;
    }

    // Validate each ticket's user info
    for (const ticketKey of selectedTicketKeys) {
      const info = ticketUserInfo[ticketKey];
      const ticketNumber = parseInt(ticketKey.split('_')[1]) + 1;
      
      if (!info.name || !info.email) {
        showError(t('purchase.ticketFormError', { number: ticketNumber }));
        // Scroll to the specific ticket form
        setTimeout(() => {
          const formElement = formRefs.current[ticketKey];
          if (formElement) {
            formElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 100);
        return;
      }

      // Get category for this ticket
      const [categoryId] = ticketKey.split('_');
      const category = event.ticketCategories?.find(c => c.id === categoryId);
      
      if (category?.ageRestrictions) {
        if (!info.dateOfBirth) {
          showError(t('purchase.dobRequiredForTicket', { number: ticketNumber }));
          setTimeout(() => {
            const formElement = formRefs.current[ticketKey];
            if (formElement) {
              formElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }
          }, 100);
          return;
        }

        const userAge = calculateAge(info.dateOfBirth);
        const restrictions = category.ageRestrictions;

        if (restrictions.minAge && userAge < restrictions.minAge) {
          showError(t('purchase.minAgeForTicket', { number: ticketNumber, age: restrictions.minAge }));
          setTimeout(() => {
            const formElement = formRefs.current[ticketKey];
            if (formElement) {
              formElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }
          }, 100);
          return;
        }

        if (restrictions.maxAge && userAge > restrictions.maxAge) {
          showError(t('purchase.maxAgeForTicket', { number: ticketNumber, age: restrictions.maxAge }));
          setTimeout(() => {
            const formElement = formRefs.current[ticketKey];
            if (formElement) {
              formElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }
          }, 100);
          return;
        }

        if (restrictions.requiresGuardian && userAge < 18) {
          if (!info.guardianName || !info.guardianEmail) {
            showError(t('purchase.guardianRequiredForTicket', { number: ticketNumber }));
            setTimeout(() => {
              const formElement = formRefs.current[ticketKey];
              if (formElement) {
                formElement.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center' 
                });
              }
            }, 100);
            return;
          }
        }
      }
    }

    // Validate age verification if required
    if (showAgeVerification && !ageVerified) {
      showError(t('purchase.ageVerificationRequired'));
      setTimeout(() => {
        purchaseSummaryRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
      return;
    }

    const selectedCategories = Object.entries(selectedTickets)
      .filter(([_, quantity]) => quantity > 0)
      .map(([categoryId, quantity]) => ({
        categoryId,
        quantity
      }));

    // Check if any selected ticket is paid
    const hasPaidTickets = selectedCategories.some(({ categoryId }) => {
      const category = event.ticketCategories?.find(c => c.id === categoryId);
      return category && !category.isFree;
    });

    // If only free tickets, process directly and redirect to success
    if (!hasPaidTickets) {
      await processFreeTickets();
    } else {
      // If has paid tickets, show payment method selection
      setPurchaseFlow('payment-methods');
    }
  };

  const processFreeTickets = async () => {
    if (!event) return;

    setPurchasing(true);

    try {
      // Authenticate anonymously before processing
      const authResponse = await apiService.loginAnonymous();
      console.log('Authenticated anonymously:', authResponse);

      const selectedCategories = Object.entries(selectedTickets)
        .filter(([_, quantity]) => quantity > 0)
        .map(([categoryId, quantity]) => ({
          categoryId,
          quantity: quantity as number
        }));

      // Create session ID for free tickets
      const freeSessionId = `free-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      setSessionId(freeSessionId);

      if (selectedCategories.length === 1) {
        // Single ticket category purchase
        const purchaseRequest: TicketPurchaseRequest = {
          eventId: event.id,
          ticketCategoryId: selectedCategories[0].categoryId,
          userId: crypto.randomUUID(),
          userEmail: Object.values(ticketUserInfo)[0]?.email || '',
          userName: Object.values(ticketUserInfo)[0]?.name || '',
          userAge: Object.values(ticketUserInfo)[0]?.dateOfBirth ? calculateAge(Object.values(ticketUserInfo)[0].dateOfBirth) : undefined,
          userDateOfBirth: Object.values(ticketUserInfo)[0]?.dateOfBirth || undefined,
          guardianConsent: needsGuardianInfo ? true : undefined,
          quantity: selectedCategories[0].quantity
        };

        await apiService.purchaseTicket(purchaseRequest);
      } else {
        // Multi-ticket purchase with individual information
        const tickets: IndividualTicket[] = [];
        
        // Create tickets based on individual user info
        Object.entries(ticketUserInfo).forEach(([ticketKey, info]) => {
          const [categoryId] = ticketKey.split('_');
          const category = event.ticketCategories?.find(c => c.id === categoryId);
          
          if (category) {
            tickets.push({
              ticketCategoryId: categoryId,
              holderName: info.name,
              holderEmail: info.email,
              holderAge: info.dateOfBirth ? calculateAge(info.dateOfBirth) : undefined,
              holderDateOfBirth: info.dateOfBirth || undefined,
              guardianConsent: (category.ageRestrictions?.requiresGuardian && 
                              info.dateOfBirth && 
                              calculateAge(info.dateOfBirth) < 18) ? true : undefined,
              quantity: 1 // Each ticket info represents one ticket
            });
          }
        });

        const purchaseRequest: MultiTicketPurchaseRequest = {
          eventId: event.id,
          purchaserUserId: crypto.randomUUID(),
          purchaserEmail: Object.values(ticketUserInfo)[0]?.email || '',
          purchaserName: Object.values(ticketUserInfo)[0]?.name || '',
          tickets
        };

        await apiService.purchaseMultipleTickets(purchaseRequest);
      }

      // Redirect to success page for free tickets
      navigate(`/checkout/success/${freeSessionId}`);

    } catch (err: any) {
      showError(err.response?.data?.error || t('purchase.failedToPurchase'));
    } finally {
      setPurchasing(false);
    }
  };

  const getPaymentMethods = (): PaymentMethod[] => {
    if (!event) return [];

    // Use payment options from event if available
    if (event.paymentOptions && event.paymentOptions.length > 0) {
      return event.paymentOptions.map(option => ({
        id: option.id,
        name: option.name,
        type: option.type,
        description: option.description,
        icon: option.icon,
        enabled: option.enabled,
        paymentUrl: option.paymentUrl,
        config: option.config
      }));
    }

    // Fallback to mock payment methods for backward compatibility
    const mockMethods: PaymentMethod[] = [
      {
        id: 'stripe',
        name: 'Cartão de Crédito/Débito',
        type: 'stripe',
        description: 'Visa, Mastercard, American Express',
        icon: '💳',
        enabled: true,
        paymentUrl: 'https://payment-processor.com/stripe'
      },
      {
        id: 'mbway',
        name: 'MB Way',
        type: 'mbway',
        description: 'Pagamento instantâneo através da app MB Way',
        icon: '📱',
        enabled: true
      },
      {
        id: 'bizum',
        name: 'Bizum',
        type: 'bizum',
        description: 'Pagamento móvel rápido e seguro',
        icon: '💰',
        enabled: false // Disabled for Portugal
      }
    ];

    return mockMethods;
  };

  const handlePaymentMethodSelect = async (method: PaymentMethod) => {
    setSelectedPaymentMethod(method);
    
    // If the payment method has a paymentUrl, redirect to that URL
    if (method.paymentUrl) {
      await handleExternalPayment(method);
    } else if (method.type === 'stripe') {
      // For card payment without external URL, show processing and redirect to default URL
      await handleCardPayment();
    } else if (method.type === 'mbway' || method.type === 'bizum') {
      // For mobile payments, show the mobile payment component
      setPurchaseFlow('mobile-payment');
    }
  };

  const handleExternalPayment = async (method: PaymentMethod) => {
    if (!event) return;

    setPurchaseFlow('processing');
    setPaymentProgress({ step: 'Initializing payment...', progress: 0, isActive: true });
    
    try {
      // If it's a Stripe payment method, use our new integration
      if (method.type === 'stripe') {
        // Authenticate anonymously before processing
        setPaymentProgress({ step: 'Authenticating...', progress: 5, isActive: true });
        await apiService.loginAnonymous();
        
        const isDevelopment = process.env.REACT_APP_USE_STRIPE_INTEGRATION !== 'true';
        
        if (isDevelopment) {
          showSuccess(`Modo desenvolvimento: Simulando ${method.name}...`);
        } else {
          showSuccess(`A criar sessão de pagamento ${method.name}...`);
        }
        
        // Get user email from first ticket holder
        const firstTicketInfo = Object.values(ticketUserInfo)[0];
        const userEmail = firstTicketInfo?.email || 'anonymous@beholdit.pt';
        
        // Initiate Stripe checkout with progress tracking
        const checkoutUrl = await apiService.initiateStripeCheckout(
          event,
          selectedTickets,
          ticketUserInfo,
          userEmail,
          (step, progress) => {
            setPaymentProgress({ step, progress, isActive: true });
          }
        );
        
        if (isDevelopment) {
          showSuccess('Simulação concluída! A redirecionar para página de sucesso...');
          setPaymentProgress({ step: 'Simulation complete!', progress: 100, isActive: true });
          // In development, simulate success after a delay
          setTimeout(() => {
            const mockSessionId = `cs_test_dev_${Date.now()}`;
            navigate(`/checkout/success/${mockSessionId}`);
          }, 2000);
        } else {
          showSuccess(`A redirecionar para ${method.name}...`);
          setPaymentProgress({ step: 'Redirecting to Stripe...', progress: 100, isActive: true });
          // Small delay to show completion before redirect
          setTimeout(() => {
            window.location.href = checkoutUrl;
          }, 500);
        }
        
      } else if (method.paymentUrl) {
        // For other external payment methods, use the configured URL
        showSuccess(`A redirecionar para ${method.name}...`);
        setPaymentProgress({ step: 'Preparing redirect...', progress: 50, isActive: true });
        
        // Simulate delay
        setTimeout(() => {
          setPaymentProgress({ step: 'Opening payment page...', progress: 75, isActive: true });
          // Redirect to the payment URL from the event configuration
          window.open(method.paymentUrl, '_blank');
          
          // For demo, redirect to success after some time
          setTimeout(() => {
            const paymentSessionId = `${method.type}-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
            navigate(`/checkout/success/${paymentSessionId}`);
          }, 3000);
        }, 2000);
      } else {
        throw new Error('Payment method not properly configured');
      }

    } catch (error: any) {
      console.error('External payment failed:', error);
      showError(error.message || t('payment.processingError'));
      setPaymentProgress({ step: '', progress: 0, isActive: false });
      setPurchaseFlow('payment-methods');
    }
  };

  const handleCardPayment = async () => {
    if (!event) return;

    setPurchaseFlow('processing');
    setPaymentProgress({ step: 'Preparing card payment...', progress: 0, isActive: true });
    
    try {
      // Authenticate anonymously before processing
      setPaymentProgress({ step: 'Authenticating...', progress: 5, isActive: true });
      await apiService.loginAnonymous();
      
      const isDevelopment = process.env.REACT_APP_USE_STRIPE_INTEGRATION !== 'true';
      
      if (isDevelopment) {
        showSuccess('Modo desenvolvimento: Simulando pagamento Stripe...');
      } else {
        showSuccess('A criar sessão de pagamento...');
      }
      
      // Get user email from first ticket holder
      const firstTicketInfo = Object.values(ticketUserInfo)[0];
      const userEmail = firstTicketInfo?.email || 'anonymous@beholdit.pt';
      
      // Initiate Stripe checkout with progress tracking
      const checkoutUrl = await apiService.initiateStripeCheckout(
        event,
        selectedTickets,
        ticketUserInfo,
        userEmail,
        (step, progress) => {
          setPaymentProgress({ step, progress, isActive: true });
        }
      );
      
      if (isDevelopment) {
        showSuccess('Simulação concluída! A redirecionar para página de sucesso...');
        setPaymentProgress({ step: 'Simulation complete!', progress: 100, isActive: true });
        // In development, simulate success after a delay
        setTimeout(() => {
          const mockSessionId = `cs_test_dev_${Date.now()}`;
          navigate(`/checkout/success/${mockSessionId}`);
        }, 2000);
      } else {
        showSuccess('A redirecionar para o Stripe...');
        setPaymentProgress({ step: 'Redirecting to Stripe...', progress: 100, isActive: true });
        // Small delay to show completion before redirect
        setTimeout(() => {
          window.location.href = checkoutUrl;
        }, 500);
      }

    } catch (error: any) {
      console.error('Card payment failed:', error);
      showError(error.message || t('payment.processingError'));
      setPaymentProgress({ step: '', progress: 0, isActive: false });
      setPurchaseFlow('payment-methods');
    }
  };

  const handleMobilePaymentSuccess = () => {
    const mobileSessionId = `mobile-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    navigate(`/checkout/success/${mobileSessionId}`);
  };

  const handleMobilePaymentError = (error: string) => {
    showError(error);
    const errorSessionId = `error-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    navigate(`/checkout/failed/${errorSessionId}`);
  };

  const handleMobilePaymentCancel = () => {
    setPurchaseFlow('payment-methods');
    setSelectedPaymentMethod(null);
  };

  const handlePaymentMethodCancel = () => {
    setPurchaseFlow('form');
    setSelectedPaymentMethod(null);
  };

  const isAvailable = (category: TicketCategory): boolean => {
    if (!category.enabled) return false;
    
    const now = new Date();
    if (category.validFrom && new Date(category.validFrom) > now) return false;
    if (category.validUntil && new Date(category.validUntil) < now) return false;
    
    const available = (category.maxAttendees || Infinity) - (category.currentSold || 0);
    return available > 0;
  };

  const getAvailableCount = (category: TicketCategory): number => {
    return (category.maxAttendees || Infinity) - (category.currentSold || 0);
  };

  if (loading) {
    return <div className="ticket-purchase loading">{t('purchase.loadingEventDetails')}</div>;
  }

  if (!event) {
    return <div className="ticket-purchase error">{t('purchase.eventNotFound')}</div>;
  }

  const needsGuardianInfo = event.ticketCategories?.some(
    category => selectedTickets[category.id] > 0 && 
    category.ageRestrictions?.requiresGuardian &&
    Object.values(ticketUserInfo).some(info => 
      info.dateOfBirth && calculateAge(info.dateOfBirth) < 18
    )
  );

  // Show payment methods selection
  if (purchaseFlow === 'payment-methods') {
    return (
      <PaymentMethods
        paymentMethods={getPaymentMethods()}
        totalAmount={getTotalCost()}
        currency={event?.ticketCategories?.[0]?.currency || 'EUR'}
        onPaymentMethodSelect={handlePaymentMethodSelect}
        onCancel={handlePaymentMethodCancel}
      />
    );
  }

  // Show mobile payment (MB Way or Bizum)
  if (purchaseFlow === 'mobile-payment' && selectedPaymentMethod) {
    return (
      <MobilePayment
        paymentMethod={selectedPaymentMethod}
        totalAmount={getTotalCost()}
        currency={event?.ticketCategories?.[0]?.currency || 'EUR'}
        sessionId={sessionId || `temp-${Date.now()}`}
        onSuccess={handleMobilePaymentSuccess}
        onCancel={handleMobilePaymentCancel}
        onError={handleMobilePaymentError}
      />
    );
  }

  // Show processing state
  if (purchaseFlow === 'processing') {
    return (
      <div className="ticket-purchase processing">
        <div className="processing-container">
          <div className="processing-spinner"></div>
          <h2>{t('payment.processingPayment')}</h2>
          {paymentProgress.isActive && (
            <div className="payment-progress-container">
              <div className="payment-progress-info">
                <div className="payment-progress-step">{paymentProgress.step}</div>
                <div className="payment-progress-percentage">{paymentProgress.progress}%</div>
              </div>
              <div className="payment-progress-bar">
                <div 
                  className="payment-progress-fill" 
                  data-progress={paymentProgress.progress}
                ></div>
              </div>
            </div>
          )}
          {!paymentProgress.isActive && (
            <p>{t('payment.initiatingPayment')}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-purchase">
      <div className="event-header">
        <div className="header-top">
          <h2>{event.title}</h2>
        </div>
        
        <div className="event-meta-cards">
          <div className="meta-card">
            <div className="meta-icon">📅</div>
            <div className="meta-content">
              <div className="meta-label">{t('purchase.eventDate')}:</div>
              <div className="meta-value">
                {new Date(event.startDateTime).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit', 
                  year: 'numeric'
                })}, {new Date(event.startDateTime).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
          
          <div className="meta-card">
            <div className="meta-icon">📍</div>
            <div className="meta-content">
              <div className="meta-label">{t('purchase.eventLocation')}:</div>
              <div className="meta-value">{event.location}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="ticket-content-scroll">
        {/* Ticket Categories */}
        <div className="ticket-categories">
        <h3>{t('purchase.selectTickets')}</h3>
        {event.ticketCategories?.map(category => (
          <div key={category.id} className={`ticket-category ${!isAvailable(category) ? 'unavailable' : ''}`}>
            <div className="ticket-card-content">
              <div className="ticket-header">
                <div className="ticket-title-section">
                  <h4 className="ticket-name">{category.name}</h4>
                  <div className="ticket-price">
                    {category.isFree ? (
                      <span className="free-price">{t('purchase.free')}</span>
                    ) : (
                      <span className="paid-price">{category.currency} {category.price.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>

              {category.description && (
                <p className="category-description">{category.description}</p>
              )}
              
              <div className="ticket-badges">
                {category.ageRestrictions && (
                  <>
                    <span className="age-badge">
                      {t('purchase.ages')}: {category.ageRestrictions.minAge || 0}{category.ageRestrictions.maxAge ? `-${category.ageRestrictions.maxAge}` : '+'}
                    </span>
                    {category.ageRestrictions.ageVerificationRequired && (
                      <span className="verification-badge">{t('purchase.ageVerification')}</span>
                    )}
                  </>
                )}
              </div>

              <div className="ticket-footer">
                <div className="availability-info">
                  <span className="availability-text">
                    {t('purchase.available')}: {category.maxAttendees ? `${getAvailableCount(category)} de ${category.maxAttendees}` : t('purchase.unlimitedAvailability')}
                  </span>
                  {category.maxQuantity && (
                    <span className="max-quantity-text">
                      {t('purchase.maxPerPurchase')}: {category.maxQuantity}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="quantity-selector">
              {isAvailable(category) ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleTicketQuantityChange(category.id, (selectedTickets[category.id] || 0) - 1)}
                    disabled={!selectedTickets[category.id] || selectedTickets[category.id] <= 0}
                    className="quantity-btn"
                  >
                    -
                  </button>
                  <span className="quantity">{selectedTickets[category.id] || 0}</span>
                  <button
                    type="button"
                    onClick={() => handleTicketQuantityChange(category.id, (selectedTickets[category.id] || 0) + 1)}
                    disabled={
                      (category.maxQuantity && (selectedTickets[category.id] || 0) >= category.maxQuantity) ||
                      (selectedTickets[category.id] || 0) >= getAvailableCount(category)
                    }
                    className="quantity-btn"
                  >
                    +
                  </button>
                </>
              ) : (
                <span className="unavailable-text">{t('purchase.unavailable')}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* User Information Form - One form per ticket */}
      {getTotalTickets() > 0 && (
        <div className="user-info-form">
          <h3>{t('purchase.ticketHolderInfo')}</h3>
          <p>{t('purchase.fillTicketInfo')}</p>
          
          {Object.entries(ticketUserInfo).map(([ticketKey, info]) => {
            const [categoryId, ticketIndex] = ticketKey.split('_');
            const category = event.ticketCategories?.find(c => c.id === categoryId);
            const ticketNumber = parseInt(ticketIndex) + 1;
            
            return (
              <div key={ticketKey} className="ticket-form-section" ref={(el) => { if (el) formRefs.current[ticketKey] = el; }}>
                <h4>{t('purchase.ticket')} {ticketNumber} - {category?.name}</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`name_${ticketKey}`}>{t('purchase.fullName')} *</label>
                    <input
                      type="text"
                      id={`name_${ticketKey}`}
                      value={info.name}
                      onChange={(e) => updateTicketUserInfo(ticketKey, 'name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`email_${ticketKey}`}>{t('purchase.email')} *</label>
                    <input
                      type="email"
                      id={`email_${ticketKey}`}
                      value={info.email}
                      onChange={(e) => updateTicketUserInfo(ticketKey, 'email', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`phone_${ticketKey}`}>{t('purchase.phone')}</label>
                    <input
                      type="tel"
                      id={`phone_${ticketKey}`}
                      value={info.phone}
                      onChange={(e) => updateTicketUserInfo(ticketKey, 'phone', e.target.value)}
                    />
                  </div>

                  {category?.ageRestrictions && (
                    <div className="form-group">
                      <label htmlFor={`dateOfBirth_${ticketKey}`}>{t('purchase.dateOfBirth')} *</label>
                      <input
                        type="date"
                        id={`dateOfBirth_${ticketKey}`}
                        value={info.dateOfBirth}
                        onChange={(e) => updateTicketUserInfo(ticketKey, 'dateOfBirth', e.target.value)}
                        required
                      />
                    </div>
                  )}
                </div>

                {/* Guardian Information for this ticket */}
                {category?.ageRestrictions?.requiresGuardian && 
                 info.dateOfBirth && 
                 calculateAge(info.dateOfBirth) < 18 && (
                  <div className="guardian-info">
                    <h5>{t('purchase.guardianInfo')} ({t('purchase.ticket')} {ticketNumber})</h5>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`guardianName_${ticketKey}`}>{t('purchase.guardianName')} *</label>
                        <input
                          type="text"
                          id={`guardianName_${ticketKey}`}
                          value={info.guardianName}
                          onChange={(e) => updateTicketUserInfo(ticketKey, 'guardianName', e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`guardianEmail_${ticketKey}`}>{t('purchase.guardianEmail')} *</label>
                        <input
                          type="email"
                          id={`guardianEmail_${ticketKey}`}
                          value={info.guardianEmail}
                          onChange={(e) => updateTicketUserInfo(ticketKey, 'guardianEmail', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor={`guardianPhone_${ticketKey}`}>{t('purchase.guardianPhone')}</label>
                      <input
                        type="tel"
                        id={`guardianPhone_${ticketKey}`}
                        value={info.guardianPhone}
                        onChange={(e) => updateTicketUserInfo(ticketKey, 'guardianPhone', e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Age Verification */}
          {showAgeVerification && (
            <div className="age-verification">
              <h4>{t('purchase.ageVerification')}</h4>
              <p>{t('purchase.ageVerificationText')}</p>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={ageVerified}
                  onChange={(e) => setAgeVerified(e.target.checked)}
                />
                {t('purchase.confirmAgeVerification')}
              </label>
            </div>
          )}
        </div>
      )}

      {/* Purchase Summary */}
      {getTotalTickets() > 0 && (
        <div className="purchase-summary" ref={purchaseSummaryRef}>
          <h3>{t('purchase.orderSummary')}</h3>
          
          <div className="summary-items">
            {Object.entries(selectedTickets)
              .filter(([_, quantity]) => quantity > 0)
              .map(([categoryId, quantity]) => {
                const category = event.ticketCategories?.find(c => c.id === categoryId);
                if (!category) return null;

                return (
                  <div key={categoryId} className="summary-item">
                    <span className="item-name">{category.name} × {quantity}</span>
                    <span className="item-price">
                      {category.isFree ? t('purchase.free') : `${category.currency} ${(category.price * quantity).toFixed(2)}`}
                    </span>
                  </div>
                );
              })}
          </div>

          <div className="summary-total">
            <div className="total-line">
              <span className="total-label">{t('purchase.totalTickets')}:</span>
              <span className="total-value">{getTotalTickets()}</span>
            </div>
            <div className="total-line">
              <span className="total-label">{t('purchase.totalCost')}:</span>
              <span className="total-value">
                {getTotalCost() === 0 ? t('purchase.free') : `${event.ticketCategories?.[0]?.currency || 'USD'} ${getTotalCost().toFixed(2)}`}
              </span>
            </div>
          </div>

          <button
            onClick={handlePurchase}
            disabled={purchasing || getTotalTickets() === 0}
            className="purchase-btn"
          >
            {purchasing ? t('purchase.processing') : getTotalCost() === 0 ? t('purchase.getFreeTickets') : t('purchase.purchaseTickets')}
          </button>
        </div>
      )}

      {/* Toast Container for alerts */}
      <ToastContainer messages={toasts} onClose={removeToast} />
      </div>
    </div>
  );
};

export default TicketPurchase;
